import type { JSX, ReactNode } from 'react';
import { ChevronRight, EyeOff } from 'lucide-react';
import { PlatformIcon } from '@/components/PlatformIcon';
import { RowActions, type RowActionItem } from '@/components/ui/row-actions';
import { channelLabel } from '@/lib/outreach-copy';
import { cn } from '@/lib/utils';
import type { LeadStep } from '@/hooks/useDiscoveryQueue';

/**
 * One creator on a worklist: the Lead queue's tabs and My creators' Messaged
 * tab render the same row, so a self-found creator and a queue lead look and
 * act the same at the same stage.
 *
 *   line 1  glyph · @handle · key stats
 *   line 2  muted context (folder · who else messaged · when you messaged)
 *   right   Found ▸ Messaged ▸ Replied, one primary button, a kebab
 *
 * Purely presentational; the caller decides the primary action and the menu.
 */
export interface LeadRowProps {
  channel: string;
  /** Without the leading @. */
  handle: string;
  /** Public profile link; the handle becomes a link when present. */
  href: string | null;
  /** Already formatted ("18.2K"); null leaves the stat out. */
  followers?: string | null;
  avgViews?: string | null;
  /** Small badge after the handle, e.g. "Assigned to you". */
  badge?: ReactNode;
  /** Second-line fragments, joined with " · ". Falsy entries are dropped. */
  meta?: ReadonlyArray<ReactNode | null | false | undefined>;
  step: LeadStep | 'skipped';
  primary?: ReactNode;
  actions?: ReadonlyArray<RowActionItem | null | undefined | false>;
  /** Left-edge accent: leads assigned to you, or to your team. */
  highlight?: 'me' | 'team' | null;
}

export function LeadRow({
  channel,
  handle,
  href,
  followers,
  avgViews,
  badge,
  meta = [],
  step,
  primary,
  actions = [],
  highlight,
}: LeadRowProps): JSX.Element {
  const metaParts = meta.filter((m): m is ReactNode => Boolean(m));
  const hasActions = actions.some(Boolean);
  return (
    <li
      className={cn(
        'flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 transition-colors hover:bg-accent/40 sm:flex-nowrap',
        highlight === 'me' && 'border-l-2 border-l-brand',
        highlight === 'team' && 'border-l-2 border-l-brand/40',
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <PlatformIcon platform={channel} size={14} className="shrink-0 text-muted-foreground" />
          {href ? (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="truncate font-semibold text-foreground underline-offset-2 hover:underline"
              title={`Open ${channelLabel(channel)} profile`}
            >
              @{handle}
            </a>
          ) : (
            <span className="truncate font-semibold">@{handle}</span>
          )}
          {badge}
          {(followers || avgViews) && (
            <span className="hidden shrink-0 items-center gap-1.5 text-xs text-muted-foreground sm:inline-flex">
              {followers && (
                <span>
                  <span className="font-mono text-foreground">{followers}</span> followers
                </span>
              )}
              {followers && avgViews && <span aria-hidden>·</span>}
              {avgViews && (
                <span>
                  <span className="font-mono text-foreground">{avgViews}</span> avg views
                </span>
              )}
            </span>
          )}
        </div>
        {(metaParts.length > 0 || followers || avgViews) && (
          <p className="mt-0.5 truncate text-xs text-muted-foreground">
            {/* Stats fold into the muted line on phones, where line 1 has no room. */}
            {(followers || avgViews) && (
              <span className="sm:hidden">
                {[followers && `${followers} followers`, avgViews && `${avgViews} avg views`]
                  .filter(Boolean)
                  .join(' · ')}
                {metaParts.length > 0 && ' · '}
              </span>
            )}
            {metaParts.map((m, i) => (
              <span key={i}>
                {i > 0 && ' · '}
                {m}
              </span>
            ))}
          </p>
        )}
      </div>

      <FlowSteps step={step} />

      <div className="ml-auto flex shrink-0 items-center gap-1">
        {primary}
        {hasActions && <RowActions items={actions} triggerLabel={`More for @${handle}`} />}
      </div>
    </li>
  );
}

const FLOW: ReadonlyArray<{ key: LeadStep; label: string }> = [
  { key: 'found', label: 'Found' },
  { key: 'messaged', label: 'Messaged' },
  { key: 'replied', label: 'Replied' },
];

/** Found ▸ Messaged ▸ Replied, with the steps reached so far in full ink. */
export function FlowSteps({ step }: { step: LeadStep | 'skipped' }): JSX.Element {
  if (step === 'skipped') {
    return (
      <span className="hidden shrink-0 items-center gap-1 text-[11px] text-muted-foreground md:inline-flex">
        <EyeOff className="h-3 w-3" />
        Skipped
      </span>
    );
  }
  const reached = FLOW.findIndex((s) => s.key === step);
  const current = FLOW[reached]?.label ?? 'Found';
  return (
    <ol
      aria-label={`Stage: ${current}`}
      title={`Stage: ${current}`}
      className="hidden shrink-0 items-center gap-0.5 text-[11px] md:flex"
    >
      {FLOW.map((s, i) => {
        const done = i <= reached;
        return (
          <li key={s.key} className="flex items-center gap-0.5">
            {i > 0 && <ChevronRight className="h-3 w-3 text-muted-foreground/50" aria-hidden />}
            <span
              className={cn(
                'inline-flex items-center gap-1',
                done ? 'font-medium text-foreground' : 'text-muted-foreground/70',
              )}
            >
              <span
                aria-hidden
                className={cn('h-1.5 w-1.5 rounded-full', done ? 'bg-brand' : 'bg-muted-foreground/30')}
              />
              {s.label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
